import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumAlcoholType } from "./enums/enumAlcoholType.generated";
import { EnumDutySource } from "./enums/enumDutySource.generated";
import { EnumEireMovementType } from "./enums/enumEireMovementType.generated";
import { EnumGermanBeerDutyMovementType } from "./enums/enumGermanBeerDutyMovementType.generated";
import { EnumGermanBeerDutyProductType } from "./enums/enumGermanBeerDutyProductType.generated";
import { EnumItemMainPurpose } from "./enums/enumItemMainPurpose.generated";
import { EnumLocationBondedStatus } from "./enums/enumLocationBondedStatus.generated";
import { EnumSpiritDutyExemptionReason } from "./enums/enumSpiritDutyExemptionReason.generated";
import { EnumStandardOrder } from "./enums/enumStandardOrder.generated";
import { EnumStockAdjustDutyImplication } from "./enums/enumStockAdjustDutyImplication.generated";

// Model Objects

export class DutySuspendedResultSet extends ModelObject<DutySuspendedResultSet> {

    public alcoholType: EnumAlcoholType = EnumAlcoholType.none;
    public dutySuspendedReceivedLitres: number = 0;
    public dutySuspendedReceivedLitresOfAlcohol: number = 0;
    public dutySuspendedSentInCountryLitres: number = 0;
    public dutySuspendedSentInCountryLitresOfAlcohol: number = 0;
    public dutySuspendedSentExportLitres: number = 0;
    public dutySuspendedSentExportLitresOfAlcohol: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.alcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.alcoholType, this.alcoholType);
          this.dutySuspendedReceivedLitres = jsonSerializedObject.dutySuspendedReceivedLitres;
          this.dutySuspendedReceivedLitresOfAlcohol = jsonSerializedObject.dutySuspendedReceivedLitresOfAlcohol;
          this.dutySuspendedSentInCountryLitres = jsonSerializedObject.dutySuspendedSentInCountryLitres;
          this.dutySuspendedSentInCountryLitresOfAlcohol = jsonSerializedObject.dutySuspendedSentInCountryLitresOfAlcohol;
          this.dutySuspendedSentExportLitres = jsonSerializedObject.dutySuspendedSentExportLitres;
          this.dutySuspendedSentExportLitresOfAlcohol = jsonSerializedObject.dutySuspendedSentExportLitresOfAlcohol;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.alcoholType = this.alcoholType.value;
        toRet.dutySuspendedReceivedLitres = this.dutySuspendedReceivedLitres;
        toRet.dutySuspendedReceivedLitresOfAlcohol = this.dutySuspendedReceivedLitresOfAlcohol;
        toRet.dutySuspendedSentInCountryLitres = this.dutySuspendedSentInCountryLitres;
        toRet.dutySuspendedSentInCountryLitresOfAlcohol = this.dutySuspendedSentInCountryLitresOfAlcohol;
        toRet.dutySuspendedSentExportLitres = this.dutySuspendedSentExportLitres;
        toRet.dutySuspendedSentExportLitresOfAlcohol = this.dutySuspendedSentExportLitresOfAlcohol;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class DutySuspendedReceiptItem extends ModelObject<DutySuspendedReceiptItem> {

    public receiptDate: LocalDate = LocalDate.ofEpochDay(0);
    public stockLocationId: Guid = Guid.createEmpty();
    public stockItemId: Guid = Guid.createEmpty();
    public quantityReceived: number = 0;
    public unitOfMeasureId: Guid = Guid.createEmpty();
    public alcoholType: EnumAlcoholType = EnumAlcoholType.none;
    public litresReceived: number = 0;
    public abv: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.receiptDate = LocalDate.parse(jsonSerializedObject.receiptDate);
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.quantityReceived = jsonSerializedObject.quantityReceived;
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.alcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.alcoholType, this.alcoholType);
          this.litresReceived = jsonSerializedObject.litresReceived;
          this.abv = jsonSerializedObject.abv;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.receiptDate = this.receiptDate.toString();
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.stockItemId = this.stockItemId.toString();
        toRet.quantityReceived = this.quantityReceived;
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.alcoholType = this.alcoholType.value;
        toRet.litresReceived = this.litresReceived;
        toRet.abv = this.abv;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "DutySuspendedReceiptItem.StockLocationId cannot be empty"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "DutySuspendedReceiptItem.StockItemId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "DutySuspendedReceiptItem.UnitOfMeasureId cannot be empty"},
    ];
}


export class DutyAuditLine extends ModelObject<DutyAuditLine> {

    public alcoholType: EnumAlcoholType = EnumAlcoholType.none;
    /**
    * The date to be used for identifying the duty rate. Typically the Despatach Date
    */
    public dateOfDutyRate: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * The date for which this item should appear on a duty return. Typically the Despatach Date, but may be different Eg for credits or corrective actions.
    */
    public dateOfDutyImplication: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * The type of entity this audit line arises from, Eg Order/Credit
    */
    public sourceType: EnumDutySource = EnumDutySource.none;
    private _sourceOutletId: Guid | null = null;
    /**
    * The outlet id associated with the entity this audit line arises from.
    */
    public get sourceOutletId(): Guid | null { return this._sourceOutletId }
    public set sourceOutletId(value: Guid | null) {
      this._sourceOutletId = value === undefined ? null : value;
    }
    /**
    * Textual reference for the entity this audit line arises from, Eg OrderNumber
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public sourceReference: string = "";
    /**
    * The Stock Item this audit line relates to.
    */
    public stockItemId: Guid = Guid.createEmpty();
    private _manufacturerId: Guid | null = null;
    /**
    * The Manufacturer of the alcoholic liquid.
    */
    public get manufacturerId(): Guid | null { return this._manufacturerId }
    public set manufacturerId(value: Guid | null) {
      this._manufacturerId = value === undefined ? null : value;
    }
    /**
    * The Abv of the item at the time of the duty implication.
    */
    public abv: number = 0;
    /**
    * The number of dutiable litres leaving bond. +ve means we Sent liquid (Eg Orders), -ve means we Received liquid (eg Credits)
    */
    public dutiableLitresLeavingBond: number = 0;
    /**
    * German beer duty movement type.
    */
    public germanBeerDutyMovementType: EnumGermanBeerDutyMovementType = EnumGermanBeerDutyMovementType.none;
    private _plato: number | null = null;
    /**
    * Beer original Plato value, if applicable and known.
    */
    public get plato(): number | null { return this._plato }
    public set plato(value: number | null) {
      this._plato = value === undefined ? null : value;
    }
    /**
    * German beer duty product type.
    */
    public germanBeerDutyProductType: EnumGermanBeerDutyProductType = EnumGermanBeerDutyProductType.none;
    private _germanBeerMixedProductPercentageA: number | null = null;
    /**
    * German beer duty percentage of beer in a mixed product, if applicable.
    */
    public get germanBeerMixedProductPercentageA(): number | null { return this._germanBeerMixedProductPercentageA }
    public set germanBeerMixedProductPercentageA(value: number | null) {
      this._germanBeerMixedProductPercentageA = value === undefined ? null : value;
    }
    /**
    * The Stock Location this audit line relates to.
    */
    public stockLocationId: Guid = Guid.createEmpty();
    /**
    * True true if this item is brewed by members of the public for their personal use by borrowing the Brewery's equipment.
    */
    public isBrewedForPersonalUse: boolean = false;
    /**
    * True if this item is designed to be connected to a pump or pressurised gas system, such as those used in pubs.
    */
    public isDesignedForPump: boolean = false;
    private _finalUnitLitres: number | null = null;
    /**
    * Litres held by the final consumable packaged unit, if relevant.
    */
    public get finalUnitLitres(): number | null { return this._finalUnitLitres }
    public set finalUnitLitres(value: number | null) {
      this._finalUnitLitres = value === undefined ? null : value;
    }
    /**
    * Reason Spirit is exempt from duty.
    */
    public spiritDutyExemptionReason: EnumSpiritDutyExemptionReason = EnumSpiritDutyExemptionReason.none;
    /**
    * Id for the entity this audit line arises from, Eg OrderId
    */
    public sourceId: Guid = Guid.createEmpty();
    /**
    * Invoice number associated with the entity this audit line arises from. Only applicable to audit lines with source type as Order.Returns empty string for other audit lines.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public sourceInvoiceNumber: string = "";
    private _dateOfProductionDutyRate: LocalDate | null = null;
    /**
    * For UK, some items have duty calculated at the rateapplicable at date of production instead of date of leaving duty suspense.
    */
    public get dateOfProductionDutyRate(): LocalDate | null { return this._dateOfProductionDutyRate }
    public set dateOfProductionDutyRate(value: LocalDate | null) {
      this._dateOfProductionDutyRate = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.alcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.alcoholType, this.alcoholType);
          this.dateOfDutyRate = LocalDate.parse(jsonSerializedObject.dateOfDutyRate);
          this.dateOfDutyImplication = LocalDate.parse(jsonSerializedObject.dateOfDutyImplication);
          this.sourceType = EnumDutySource.getByValue(jsonSerializedObject.sourceType, this.sourceType);
          this.sourceOutletId = jsonSerializedObject.sourceOutletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.sourceOutletId);
          this.sourceReference = jsonSerializedObject.sourceReference;
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.manufacturerId = jsonSerializedObject.manufacturerId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.manufacturerId);
          this.abv = jsonSerializedObject.abv;
          this.dutiableLitresLeavingBond = jsonSerializedObject.dutiableLitresLeavingBond;
          this.germanBeerDutyMovementType = EnumGermanBeerDutyMovementType.getByValue(jsonSerializedObject.germanBeerDutyMovementType, this.germanBeerDutyMovementType);
          this.plato = jsonSerializedObject.plato == null ? null : jsonSerializedObject.plato;
          this.germanBeerDutyProductType = EnumGermanBeerDutyProductType.getByValue(jsonSerializedObject.germanBeerDutyProductType, this.germanBeerDutyProductType);
          this.germanBeerMixedProductPercentageA = jsonSerializedObject.germanBeerMixedProductPercentageA == null ? null : jsonSerializedObject.germanBeerMixedProductPercentageA;
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.isBrewedForPersonalUse = jsonSerializedObject.isBrewedForPersonalUse;
          this.isDesignedForPump = jsonSerializedObject.isDesignedForPump;
          this.finalUnitLitres = jsonSerializedObject.finalUnitLitres == null ? null : jsonSerializedObject.finalUnitLitres;
          this.spiritDutyExemptionReason = EnumSpiritDutyExemptionReason.getByValue(jsonSerializedObject.spiritDutyExemptionReason, this.spiritDutyExemptionReason);
          this.sourceId = Guid.parseOrEmpty(jsonSerializedObject.sourceId);
          this.sourceInvoiceNumber = jsonSerializedObject.sourceInvoiceNumber;
          this.dateOfProductionDutyRate = jsonSerializedObject.dateOfProductionDutyRate == null ? null : LocalDate.parse(jsonSerializedObject.dateOfProductionDutyRate);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.alcoholType = this.alcoholType.value;
        toRet.dateOfDutyRate = this.dateOfDutyRate.toString();
        toRet.dateOfDutyImplication = this.dateOfDutyImplication.toString();
        toRet.sourceType = this.sourceType.value;
        toRet.sourceOutletId = this.sourceOutletId == null ? null : this.sourceOutletId.toString();
        toRet.sourceReference = this.sourceReference;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.manufacturerId = this.manufacturerId == null ? null : this.manufacturerId.toString();
        toRet.abv = this.abv;
        toRet.dutiableLitresLeavingBond = this.dutiableLitresLeavingBond;
        toRet.germanBeerDutyMovementType = this.germanBeerDutyMovementType.value;
        toRet.plato = this.plato == null ? null : this.plato;
        toRet.germanBeerDutyProductType = this.germanBeerDutyProductType.value;
        toRet.germanBeerMixedProductPercentageA = this.germanBeerMixedProductPercentageA == null ? null : this.germanBeerMixedProductPercentageA;
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.isBrewedForPersonalUse = this.isBrewedForPersonalUse;
        toRet.isDesignedForPump = this.isDesignedForPump;
        toRet.finalUnitLitres = this.finalUnitLitres == null ? null : this.finalUnitLitres;
        toRet.spiritDutyExemptionReason = this.spiritDutyExemptionReason.value;
        toRet.sourceId = this.sourceId.toString();
        toRet.sourceInvoiceNumber = this.sourceInvoiceNumber;
        toRet.dateOfProductionDutyRate = this.dateOfProductionDutyRate == null ? null : this.dateOfProductionDutyRate.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "sourceOutletId", rule: (v: any) => v == null || !v.isEmpty() || "DutyAuditLine.SourceOutletId cannot be empty"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "DutyAuditLine.StockItemId cannot be empty"},
            {field: "manufacturerId", rule: (v: any) => v == null || !v.isEmpty() || "DutyAuditLine.ManufacturerId cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "DutyAuditLine.StockLocationId cannot be empty"},
            {field: "sourceId", rule: (v: any) => !v.isEmpty() || "DutyAuditLine.SourceId cannot be empty"},
    ];
}


export class EvaluateDutyRequestItem extends ModelObject<EvaluateDutyRequestItem> {

    /**
    * An Id which will be copied to result for this item, to allow correlation of results to query lines
    */
    public correlationId: Guid = Guid.createEmpty();
    /**
    * Date of leaving duty suspense.
    */
    public dutyDate: LocalDate = LocalDate.ofEpochDay(0);
    public dutySource: EnumDutySource = EnumDutySource.none;
    private _stockLocationId: Guid | null = null;
    /**
    * Stock Location the item leaves. May be null in case of Spoilt Beer Credit.
    */
    public get stockLocationId(): Guid | null { return this._stockLocationId }
    public set stockLocationId(value: Guid | null) {
      this._stockLocationId = value === undefined ? null : value;
    }
    public alcoholType: EnumAlcoholType = EnumAlcoholType.none;
    public abv: number = 0;
    public isSpoilt: boolean = false;
    private _manufacturerId: Guid | null = null;
    public get manufacturerId(): Guid | null { return this._manufacturerId }
    public set manufacturerId(value: Guid | null) {
      this._manufacturerId = value === undefined ? null : value;
    }
    public quantityLeavingLocation: number = 0;
    public unitDutiableLitres: number = 0;
    public standardOrderType: EnumStandardOrder = EnumStandardOrder.fullPriceDutyPaid;
    public germanBeerDutyMovementType: EnumGermanBeerDutyMovementType = EnumGermanBeerDutyMovementType.none;
    public germanBeerDutyProductType: EnumGermanBeerDutyProductType = EnumGermanBeerDutyProductType.none;
    private _germanBeerMixedProductPercentageA: number | null = null;
    public get germanBeerMixedProductPercentageA(): number | null { return this._germanBeerMixedProductPercentageA }
    public set germanBeerMixedProductPercentageA(value: number | null) {
      this._germanBeerMixedProductPercentageA = value === undefined ? null : value;
    }
    private _plato: number | null = null;
    public get plato(): number | null { return this._plato }
    public set plato(value: number | null) {
      this._plato = value === undefined ? null : value;
    }
    public isBrewedForPersonalUse: boolean = false;
    public isDesignedForPump: boolean = false;
    private _finalUnitLitres: number | null = null;
    public get finalUnitLitres(): number | null { return this._finalUnitLitres }
    public set finalUnitLitres(value: number | null) {
      this._finalUnitLitres = value === undefined ? null : value;
    }
    public spiritDutyExemptionReason: EnumSpiritDutyExemptionReason = EnumSpiritDutyExemptionReason.none;
    public eireDutyMovementType: EnumEireMovementType = EnumEireMovementType.unknown;
    private _dateOfProductionDutyRate: LocalDate | null = null;
    /**
    * For UK, some items have duty calculated at the rateapplicable at date of production instead of date of leaving duty suspense.
    */
    public get dateOfProductionDutyRate(): LocalDate | null { return this._dateOfProductionDutyRate }
    public set dateOfProductionDutyRate(value: LocalDate | null) {
      this._dateOfProductionDutyRate = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.correlationId = Guid.parseOrEmpty(jsonSerializedObject.correlationId);
          this.dutyDate = LocalDate.parse(jsonSerializedObject.dutyDate);
          this.dutySource = EnumDutySource.getByValue(jsonSerializedObject.dutySource, this.dutySource);
          this.stockLocationId = jsonSerializedObject.stockLocationId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.alcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.alcoholType, this.alcoholType);
          this.abv = jsonSerializedObject.abv;
          this.isSpoilt = jsonSerializedObject.isSpoilt;
          this.manufacturerId = jsonSerializedObject.manufacturerId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.manufacturerId);
          this.quantityLeavingLocation = jsonSerializedObject.quantityLeavingLocation;
          this.unitDutiableLitres = jsonSerializedObject.unitDutiableLitres;
          this.standardOrderType = EnumStandardOrder.getByValue(jsonSerializedObject.standardOrderType, this.standardOrderType);
          this.germanBeerDutyMovementType = EnumGermanBeerDutyMovementType.getByValue(jsonSerializedObject.germanBeerDutyMovementType, this.germanBeerDutyMovementType);
          this.germanBeerDutyProductType = EnumGermanBeerDutyProductType.getByValue(jsonSerializedObject.germanBeerDutyProductType, this.germanBeerDutyProductType);
          this.germanBeerMixedProductPercentageA = jsonSerializedObject.germanBeerMixedProductPercentageA == null ? null : jsonSerializedObject.germanBeerMixedProductPercentageA;
          this.plato = jsonSerializedObject.plato == null ? null : jsonSerializedObject.plato;
          this.isBrewedForPersonalUse = jsonSerializedObject.isBrewedForPersonalUse;
          this.isDesignedForPump = jsonSerializedObject.isDesignedForPump;
          this.finalUnitLitres = jsonSerializedObject.finalUnitLitres == null ? null : jsonSerializedObject.finalUnitLitres;
          this.spiritDutyExemptionReason = EnumSpiritDutyExemptionReason.getByValue(jsonSerializedObject.spiritDutyExemptionReason, this.spiritDutyExemptionReason);
          this.eireDutyMovementType = EnumEireMovementType.getByValue(jsonSerializedObject.eireDutyMovementType, this.eireDutyMovementType);
          this.dateOfProductionDutyRate = jsonSerializedObject.dateOfProductionDutyRate == null ? null : LocalDate.parse(jsonSerializedObject.dateOfProductionDutyRate);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.correlationId = this.correlationId.toString();
        toRet.dutyDate = this.dutyDate.toString();
        toRet.dutySource = this.dutySource.value;
        toRet.stockLocationId = this.stockLocationId == null ? null : this.stockLocationId.toString();
        toRet.alcoholType = this.alcoholType.value;
        toRet.abv = this.abv;
        toRet.isSpoilt = this.isSpoilt;
        toRet.manufacturerId = this.manufacturerId == null ? null : this.manufacturerId.toString();
        toRet.quantityLeavingLocation = this.quantityLeavingLocation;
        toRet.unitDutiableLitres = this.unitDutiableLitres;
        toRet.standardOrderType = this.standardOrderType.value;
        toRet.germanBeerDutyMovementType = this.germanBeerDutyMovementType.value;
        toRet.germanBeerDutyProductType = this.germanBeerDutyProductType.value;
        toRet.germanBeerMixedProductPercentageA = this.germanBeerMixedProductPercentageA == null ? null : this.germanBeerMixedProductPercentageA;
        toRet.plato = this.plato == null ? null : this.plato;
        toRet.isBrewedForPersonalUse = this.isBrewedForPersonalUse;
        toRet.isDesignedForPump = this.isDesignedForPump;
        toRet.finalUnitLitres = this.finalUnitLitres == null ? null : this.finalUnitLitres;
        toRet.spiritDutyExemptionReason = this.spiritDutyExemptionReason.value;
        toRet.eireDutyMovementType = this.eireDutyMovementType.value;
        toRet.dateOfProductionDutyRate = this.dateOfProductionDutyRate == null ? null : this.dateOfProductionDutyRate.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "correlationId", rule: (v: any) => !v.isEmpty() || "EvaluateDutyRequestItem.CorrelationId cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => v == null || !v.isEmpty() || "EvaluateDutyRequestItem.StockLocationId cannot be empty"},
            {field: "manufacturerId", rule: (v: any) => v == null || !v.isEmpty() || "EvaluateDutyRequestItem.ManufacturerId cannot be empty"},
    ];
}


export class EvaluateDutyResponseItem extends ModelObject<EvaluateDutyResponseItem> {

    /**
    * Returns the same value as provided in the request item
    */
    public correlationId: Guid = Guid.createEmpty();
    /**
    * Duty cost due to relevant item. -ve means the tenant's duty liability is decreased, eg for a credit where quantityLeavingLocation is -ve.
    */
    public dutyCostUnrounded: number = 0;
    /**
    * True if duty will be incurred directly due to the line (Eg sale from a bonded location). False indicates that the duty cost will be incurred in a manner not directly related to this line (Eg sale from a non-bonded location where the duty  would have been incurred when the stock was transferred to the location.)
    */
    public isDutyDirectlyIncurred: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.correlationId = Guid.parseOrEmpty(jsonSerializedObject.correlationId);
          this.dutyCostUnrounded = jsonSerializedObject.dutyCostUnrounded;
          this.isDutyDirectlyIncurred = jsonSerializedObject.isDutyDirectlyIncurred;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.correlationId = this.correlationId.toString();
        toRet.dutyCostUnrounded = this.dutyCostUnrounded;
        toRet.isDutyDirectlyIncurred = this.isDutyDirectlyIncurred;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "correlationId", rule: (v: any) => !v.isEmpty() || "EvaluateDutyResponseItem.CorrelationId cannot be empty"},
    ];
}


export class FullStockAuditLine extends ModelObject<FullStockAuditLine> {

    /**
    * The Stock Location this audit line relates to.
    */
    public stockLocationId: Guid = Guid.createEmpty();
    public isBonded: boolean = false;
    public isExternal: boolean = false;
    /**
    * The type of entity this audit line arises from, Eg Order/Credit
    */
    public sourceType: EnumDutySource = EnumDutySource.none;
    /**
    * Textual reference for the entity this audit line arises from, Eg OrderNumber
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public sourceReference: string = "";
    /**
    * The Id of the source item. Will be a reference suitable to SourceType.
    */
    public sourceId: Guid = Guid.createEmpty();
    /**
    * Identifies if the stock moved across a bonded boundary.
    */
    public didCrossBondedBoundary: boolean = false;
    /**
    * The date of the stock movement, in particular for identifying which duty return period this relates to.
    */
    public date: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * The date to be used for identifying the duty rate. Often same as Date but occasionally not (Eg credits).
    */
    public dateOfDutyRate: LocalDate = LocalDate.ofEpochDay(0);
    private _sourceOutletId: Guid | null = null;
    /**
    * The outlet id associated with the entity this audit line arises from.
    */
    public get sourceOutletId(): Guid | null { return this._sourceOutletId }
    public set sourceOutletId(value: Guid | null) {
      this._sourceOutletId = value === undefined ? null : value;
    }
    /**
    * The duty implication as best inferred from all the information about the movement. For example, orders and credits will infer from StandardOrderType, transfers will infer from the bonded and non-bonded nature of source and dest.
    */
    public inferredDutyImplication: EnumStockAdjustDutyImplication = EnumStockAdjustDutyImplication.noDuty;
    /**
    * Identifies if the stock is not yet committed into stock figures. This could be the case for orders/credits which are complete but not yet historic.
    */
    public isPendingStockFigureUpdate: boolean = false;
    public alcoholType: EnumAlcoholType = EnumAlcoholType.none;
    /**
    * The Stock Item this audit line relates to.
    */
    public stockItemId: Guid = Guid.createEmpty();
    private _manufacturerId: Guid | null = null;
    /**
    * The Manufacturer of the alcoholic liquid.
    */
    public get manufacturerId(): Guid | null { return this._manufacturerId }
    public set manufacturerId(value: Guid | null) {
      this._manufacturerId = value === undefined ? null : value;
    }
    /**
    * The Abv of the item at the time of the duty implication.
    */
    public abv: number = 0;
    /**
    * German beer duty movement type.
    */
    public germanBeerDutyMovementType: EnumGermanBeerDutyMovementType = EnumGermanBeerDutyMovementType.none;
    private _plato: number | null = null;
    /**
    * Beer original Plato value, if applicable and known.
    */
    public get plato(): number | null { return this._plato }
    public set plato(value: number | null) {
      this._plato = value === undefined ? null : value;
    }
    /**
    * German beer duty product type.
    */
    public germanBeerDutyProductType: EnumGermanBeerDutyProductType = EnumGermanBeerDutyProductType.none;
    private _germanBeerMixedProductPercentageA: number | null = null;
    /**
    * German beer duty percentage of beer in a mixed product, if applicable.
    */
    public get germanBeerMixedProductPercentageA(): number | null { return this._germanBeerMixedProductPercentageA }
    public set germanBeerMixedProductPercentageA(value: number | null) {
      this._germanBeerMixedProductPercentageA = value === undefined ? null : value;
    }
    /**
    * True true if this item is brewed by members of the public for their personal use by borrowing the Brewery's equipment.
    */
    public isBrewedForPersonalUse: boolean = false;
    /**
    * True if this item is designed to be connected to a pump or pressurised gas system, such as those used in pubs.
    */
    public isDesignedForPump: boolean = false;
    private _finalUnitLitres: number | null = null;
    /**
    * Litres held by the final consumable packaged unit, if relevant.
    */
    public get finalUnitLitres(): number | null { return this._finalUnitLitres }
    public set finalUnitLitres(value: number | null) {
      this._finalUnitLitres = value === undefined ? null : value;
    }
    /**
    * Reason Spirit is exempt from duty.
    */
    public spiritDutyExemptionReason: EnumSpiritDutyExemptionReason = EnumSpiritDutyExemptionReason.none;
    /**
    * Quantity of relevant units leaving the given location. Will be -ve if units arrived.
    */
    public quantityLeaving: number = 0;
    public unitOfMeasureId: Guid = Guid.createEmpty();
    private _packageTypeId: Guid | null = null;
    public get packageTypeId(): Guid | null { return this._packageTypeId }
    public set packageTypeId(value: Guid | null) {
      this._packageTypeId = value === undefined ? null : value;
    }
    private _unitDutiableLitres: number | null = null;
    /**
    * Dutiable litres per unit of measure, if defined.
    */
    public get unitDutiableLitres(): number | null { return this._unitDutiableLitres }
    public set unitDutiableLitres(value: number | null) {
      this._unitDutiableLitres = value === undefined ? null : value;
    }
    private _unitLitres: number | null = null;
    /**
    * Litres per unit, if known. May come from the source entity itself (Eg OrderLine), the Unit of Measure or the Package Type.
    */
    public get unitLitres(): number | null { return this._unitLitres }
    public set unitLitres(value: number | null) {
      this._unitLitres = value === undefined ? null : value;
    }
    /**
    * Eire alcohol movement type.
    */
    public eireMovementType: EnumEireMovementType = EnumEireMovementType.unknown;
    /**
    * Invoice number associated with the entity this audit line arises from. Only applicable to audit lines with source type as Order.Returns empty string for other audit lines.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public sourceInvoiceNumber: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.isBonded = jsonSerializedObject.isBonded;
          this.isExternal = jsonSerializedObject.isExternal;
          this.sourceType = EnumDutySource.getByValue(jsonSerializedObject.sourceType, this.sourceType);
          this.sourceReference = jsonSerializedObject.sourceReference;
          this.sourceId = Guid.parseOrEmpty(jsonSerializedObject.sourceId);
          this.didCrossBondedBoundary = jsonSerializedObject.didCrossBondedBoundary;
          this.date = LocalDate.parse(jsonSerializedObject.date);
          this.dateOfDutyRate = LocalDate.parse(jsonSerializedObject.dateOfDutyRate);
          this.sourceOutletId = jsonSerializedObject.sourceOutletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.sourceOutletId);
          this.inferredDutyImplication = EnumStockAdjustDutyImplication.getByValue(jsonSerializedObject.inferredDutyImplication, this.inferredDutyImplication);
          this.isPendingStockFigureUpdate = jsonSerializedObject.isPendingStockFigureUpdate;
          this.alcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.alcoholType, this.alcoholType);
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.manufacturerId = jsonSerializedObject.manufacturerId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.manufacturerId);
          this.abv = jsonSerializedObject.abv;
          this.germanBeerDutyMovementType = EnumGermanBeerDutyMovementType.getByValue(jsonSerializedObject.germanBeerDutyMovementType, this.germanBeerDutyMovementType);
          this.plato = jsonSerializedObject.plato == null ? null : jsonSerializedObject.plato;
          this.germanBeerDutyProductType = EnumGermanBeerDutyProductType.getByValue(jsonSerializedObject.germanBeerDutyProductType, this.germanBeerDutyProductType);
          this.germanBeerMixedProductPercentageA = jsonSerializedObject.germanBeerMixedProductPercentageA == null ? null : jsonSerializedObject.germanBeerMixedProductPercentageA;
          this.isBrewedForPersonalUse = jsonSerializedObject.isBrewedForPersonalUse;
          this.isDesignedForPump = jsonSerializedObject.isDesignedForPump;
          this.finalUnitLitres = jsonSerializedObject.finalUnitLitres == null ? null : jsonSerializedObject.finalUnitLitres;
          this.spiritDutyExemptionReason = EnumSpiritDutyExemptionReason.getByValue(jsonSerializedObject.spiritDutyExemptionReason, this.spiritDutyExemptionReason);
          this.quantityLeaving = jsonSerializedObject.quantityLeaving;
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.packageTypeId = jsonSerializedObject.packageTypeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.packageTypeId);
          this.unitDutiableLitres = jsonSerializedObject.unitDutiableLitres == null ? null : jsonSerializedObject.unitDutiableLitres;
          this.unitLitres = jsonSerializedObject.unitLitres == null ? null : jsonSerializedObject.unitLitres;
          this.eireMovementType = EnumEireMovementType.getByValue(jsonSerializedObject.eireMovementType, this.eireMovementType);
          this.sourceInvoiceNumber = jsonSerializedObject.sourceInvoiceNumber;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.isBonded = this.isBonded;
        toRet.isExternal = this.isExternal;
        toRet.sourceType = this.sourceType.value;
        toRet.sourceReference = this.sourceReference;
        toRet.sourceId = this.sourceId.toString();
        toRet.didCrossBondedBoundary = this.didCrossBondedBoundary;
        toRet.date = this.date.toString();
        toRet.dateOfDutyRate = this.dateOfDutyRate.toString();
        toRet.sourceOutletId = this.sourceOutletId == null ? null : this.sourceOutletId.toString();
        toRet.inferredDutyImplication = this.inferredDutyImplication.value;
        toRet.isPendingStockFigureUpdate = this.isPendingStockFigureUpdate;
        toRet.alcoholType = this.alcoholType.value;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.manufacturerId = this.manufacturerId == null ? null : this.manufacturerId.toString();
        toRet.abv = this.abv;
        toRet.germanBeerDutyMovementType = this.germanBeerDutyMovementType.value;
        toRet.plato = this.plato == null ? null : this.plato;
        toRet.germanBeerDutyProductType = this.germanBeerDutyProductType.value;
        toRet.germanBeerMixedProductPercentageA = this.germanBeerMixedProductPercentageA == null ? null : this.germanBeerMixedProductPercentageA;
        toRet.isBrewedForPersonalUse = this.isBrewedForPersonalUse;
        toRet.isDesignedForPump = this.isDesignedForPump;
        toRet.finalUnitLitres = this.finalUnitLitres == null ? null : this.finalUnitLitres;
        toRet.spiritDutyExemptionReason = this.spiritDutyExemptionReason.value;
        toRet.quantityLeaving = this.quantityLeaving;
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.packageTypeId = this.packageTypeId == null ? null : this.packageTypeId.toString();
        toRet.unitDutiableLitres = this.unitDutiableLitres == null ? null : this.unitDutiableLitres;
        toRet.unitLitres = this.unitLitres == null ? null : this.unitLitres;
        toRet.eireMovementType = this.eireMovementType.value;
        toRet.sourceInvoiceNumber = this.sourceInvoiceNumber;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "FullStockAuditLine.StockLocationId cannot be empty"},
            {field: "sourceId", rule: (v: any) => !v.isEmpty() || "FullStockAuditLine.SourceId cannot be empty"},
            {field: "sourceOutletId", rule: (v: any) => v == null || !v.isEmpty() || "FullStockAuditLine.SourceOutletId cannot be empty"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "FullStockAuditLine.StockItemId cannot be empty"},
            {field: "manufacturerId", rule: (v: any) => v == null || !v.isEmpty() || "FullStockAuditLine.ManufacturerId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "FullStockAuditLine.UnitOfMeasureId cannot be empty"},
            {field: "packageTypeId", rule: (v: any) => v == null || !v.isEmpty() || "FullStockAuditLine.PackageTypeId cannot be empty"},
    ];
}


export class DutySuspendedOrderAuditLine extends ModelObject<DutySuspendedOrderAuditLine> {

    public orderNumber: string = "";
    public creditNumber: string = "";
    public despatchDate: LocalDate = LocalDate.ofEpochDay(0);
    public outletId: Guid = Guid.createEmpty();
    public alcoholType: EnumAlcoholType = EnumAlcoholType.none;
    public stockItemId: Guid = Guid.createEmpty();
    public productName: string = "";
    public quantity: number = 0;
    public totalNetPrice: number = 0;
    public totalHectolitres: number = 0;
    public abv: number = 0;
    public totalLitresOfAlcohol: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.orderNumber = jsonSerializedObject.orderNumber;
          this.creditNumber = jsonSerializedObject.creditNumber;
          this.despatchDate = LocalDate.parse(jsonSerializedObject.despatchDate);
          this.outletId = Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.alcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.alcoholType, this.alcoholType);
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.productName = jsonSerializedObject.productName;
          this.quantity = jsonSerializedObject.quantity;
          this.totalNetPrice = jsonSerializedObject.totalNetPrice;
          this.totalHectolitres = jsonSerializedObject.totalHectolitres;
          this.abv = jsonSerializedObject.abv;
          this.totalLitresOfAlcohol = jsonSerializedObject.totalLitresOfAlcohol;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.orderNumber = this.orderNumber;
        toRet.creditNumber = this.creditNumber;
        toRet.despatchDate = this.despatchDate.toString();
        toRet.outletId = this.outletId.toString();
        toRet.alcoholType = this.alcoholType.value;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.productName = this.productName;
        toRet.quantity = this.quantity;
        toRet.totalNetPrice = this.totalNetPrice;
        toRet.totalHectolitres = this.totalHectolitres;
        toRet.abv = this.abv;
        toRet.totalLitresOfAlcohol = this.totalLitresOfAlcohol;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "outletId", rule: (v: any) => !v.isEmpty() || "DutySuspendedOrderAuditLine.OutletId cannot be empty"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "DutySuspendedOrderAuditLine.StockItemId cannot be empty"},
    ];
}


export class ProductionDutyRateDateRange extends ModelObject<ProductionDutyRateDateRange> {

    private _fromDate: LocalDate | null = null;
    /**
    * Earliest date in range, null means start of time.
    */
    public get fromDate(): LocalDate | null { return this._fromDate }
    public set fromDate(value: LocalDate | null) {
      this._fromDate = value === undefined ? null : value;
    }
    private _toDate: LocalDate | null = null;
    /**
    * Latest date in range, null means end date is in future but not yet known.
    */
    public get toDate(): LocalDate | null { return this._toDate }
    public set toDate(value: LocalDate | null) {
      this._toDate = value === undefined ? null : value;
    }
    /**
    * Preferred single date to use to represent this range.
    */
    public preferredValue: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * Friendly name to represent this range.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public name: string = "";
    /**
    * Identifies if range should be hidden by default. Typically we only want to show the current range and the immediately previous range only.
    */
    public isHidden: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.fromDate = jsonSerializedObject.fromDate == null ? null : LocalDate.parse(jsonSerializedObject.fromDate);
          this.toDate = jsonSerializedObject.toDate == null ? null : LocalDate.parse(jsonSerializedObject.toDate);
          this.preferredValue = LocalDate.parse(jsonSerializedObject.preferredValue);
          this.name = jsonSerializedObject.name;
          this.isHidden = jsonSerializedObject.isHidden;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.fromDate = this.fromDate == null ? null : this.fromDate.toString();
        toRet.toDate = this.toDate == null ? null : this.toDate.toString();
        toRet.preferredValue = this.preferredValue.toString();
        toRet.name = this.name;
        toRet.isHidden = this.isHidden;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class DutyService extends ServiceBase {

  public async evaluateDutySuspendedMovements(fromDateInclusive: LocalDate, toDateInclusive: LocalDate, limitedAlcoholTypes: readonly EnumAlcoholType[] | null, limitedMainPurposes: readonly EnumItemMainPurpose[] | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(DutySuspendedResultSet)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    fromDateInclusive:fromDateInclusive.toString(),
    toDateInclusive:toDateInclusive.toString(),
    limitedAlcoholTypes:limitedAlcoholTypes == null ? null : limitedAlcoholTypes.map(v => v.value),
    limitedMainPurposes:limitedMainPurposes == null ? null : limitedMainPurposes.map(v => v.value),
    };
    const extractor = (response:any) => response.results.map((v: any) => new DutySuspendedResultSet(v));;
    return this.makeJsonRequest("duty/v1/evaluateDutySuspendedMovements", req, false, extractor, opts);
  };

  public async getDutySuspendedReceipts(fromDateInclusive: LocalDate, toDateInclusive: LocalDate, limitToGivenAlcoholTypes: boolean, limitedAlcoholTypes: readonly EnumAlcoholType[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(DutySuspendedReceiptItem)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    fromDateInclusive:fromDateInclusive.toString(),
    toDateInclusive:toDateInclusive.toString(),
    limitToGivenAlcoholTypes:limitToGivenAlcoholTypes,
    limitedAlcoholTypes:limitedAlcoholTypes.map(v => v.value),
    };
    const extractor = (response:any) => response.results.map((v: any) => new DutySuspendedReceiptItem(v));;
    return this.makeJsonRequest("duty/v1/getDutySuspendedReceipts", req, false, extractor, opts);
  };

  public async getDutyAuditInformation(fromDateInclusive: LocalDate, toDateInclusive: LocalDate, limitedAlcoholTypes: readonly EnumAlcoholType[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(DutyAuditLine)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    fromDateInclusive:fromDateInclusive.toString(),
    toDateInclusive:toDateInclusive.toString(),
    limitedAlcoholTypes:limitedAlcoholTypes.map(v => v.value),
    };
    const extractor = (response:any) => response.results.map((v: any) => new DutyAuditLine(v));;
    return this.makeJsonRequest("duty/v1/getDutyAuditInformation", req, false, extractor, opts);
  };

  public async evaluateDutyForItems(items: readonly EvaluateDutyRequestItem[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(EvaluateDutyResponseItem)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    items:items.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => response.results.map((v: any) => new EvaluateDutyResponseItem(v));;
    return this.makeJsonRequest("duty/v1/evaluateDutyForItems", req, false, extractor, opts);
  };

  public async getFullStockAudit(fromDateInclusive: LocalDate, toDateInclusive: LocalDate | null, alcoholTypes: readonly EnumAlcoholType[], bondedStatuses: readonly EnumLocationBondedStatus[] | null, sources: readonly EnumDutySource[] | null, includePendingStockUpdates: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(FullStockAuditLine)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    fromDateInclusive:fromDateInclusive.toString(),
    toDateInclusive:toDateInclusive == null ? null : toDateInclusive.toString(),
    alcoholTypes:alcoholTypes.map(v => v.value),
    bondedStatuses:bondedStatuses == null ? null : bondedStatuses.map(v => v.value),
    sources:sources == null ? null : sources.map(v => v.value),
    includePendingStockUpdates:includePendingStockUpdates,
    };
    const extractor = (response:any) => response.results.map((v: any) => new FullStockAuditLine(v));;
    return this.makeJsonRequest("duty/v1/getFullStockAudit", req, false, extractor, opts);
  };

  public async evaluateDutySuspendedOrders(fromDateInclusive: LocalDate, toDateInclusive: LocalDate, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(DutySuspendedOrderAuditLine)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    fromDateInclusive:fromDateInclusive.toString(),
    toDateInclusive:toDateInclusive.toString(),
    };
    const extractor = (response:any) => response.results.map((v: any) => new DutySuspendedOrderAuditLine(v));;
    return this.makeJsonRequest("duty/v1/evaluateDutySuspendedOrders", req, false, extractor, opts);
  };

  public async getAllProductionDutyDateRanges(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(ProductionDutyRateDateRange)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.ranges.map((v: any) => new ProductionDutyRateDateRange(v));;
    return this.makeJsonRequest("duty/v1/getAllProductionDutyDateRanges", req, false, extractor, opts);
  };
}

export const dutyService = new DutyService();